import { useState, ChangeEvent, useContext, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import useDeepCompareEffect from '../../hooks/useDeepCompareEffect';
import Select from 'react-select';
import { customStylesShorterXL } from '../../utils/customFormStyles';
import FormErrorModal from '../../components/Modals/FormErrorModal';
import { getIndustryTypeOptions, getSubIndustryOptions } from '../../utils/formOptions';
import TypesContext from '../../context/TypesContext';
import InteractionDataContext from '../../context/InteractionDataContext';
import { Company, IndustryType } from '../../services/types';
import { calculateYearByType } from '../../utils/helpers';
import BackButtonHeader from '../../components/Navigation/BackButtonHeader';
import CompaniesContext from '../../context/CompaniesContext';
import { Loader } from '@googlemaps/js-api-loader';

// enable typing for window.google
declare global {
    interface Window {
        google: any;
    }
}

function EditCompany() {
    const { typesData } = useContext(TypesContext);
    const { setInteractionsYear } = useContext(InteractionDataContext);
    const { fetchCompanies } = useContext(CompaniesContext);

    const [tradeName, setTradeName] = useState("");
    const [legalName, setLegalName] = useState("");
    const [ubi, setUbi] = useState("");
    const [externalId, setExternalId] = useState("");
    const [hasUBI, setHasUBI] = useState(true);
    const [industry, setIndustry] = useState<string>("");
    const [subIndustry, setSubIndustry] = useState<string>("");
    const [subIndustryOptions, setSubIndustryOptions] = useState<{ value: string; label: string; }[]>([]);
    const [address, setAddress] = useState("");
    const [latitude, setLatitude] = useState("");
    const [longitude, setLongitude] = useState("");

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formErrorMsg, setFormErrorMsg] = useState('');
    const autocompleteRef = useRef<HTMLInputElement>(null);

    const currentFisYear = calculateYearByType('');

    const navigate = useNavigate();
    const { id } = useParams();

    // Create select options for industry
    const industryTypeOptions = getIndustryTypeOptions(typesData, 'industry_types', industry);

    useEffect(() => {
        if (industry) {
            const selectedIndustry = typesData.industry_types.find((type: IndustryType) => type.code === industry);
            const options = getSubIndustryOptions(selectedIndustry);
            setSubIndustryOptions(options);
        } else {
            setSubIndustryOptions([]);
        }
    }, [industry, typesData]);

    // Handler for navigating back to Companies page
    const navigateBack = () => {
        navigate(-1);
    };

    useDeepCompareEffect(() => {

        const fetchCurrentCompany = async () => {

            if (!id) {
                console.log('Id not found, skipping fetch.');
                return;
            }

            try {
                const response = await fetch(`${import.meta.env.VITE_COMMERCE_BACKEND_URL}/api/getcompanydetails?companyId=${id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });
        
                const responseData = await response.json();
        
                if (!response.ok) {
                    throw new Error(responseData.error || 'Failed to fetch target');
                }
        
                console.log('Data successfully fetched from backend:', responseData);
                
                // Grab first interaction in returned data array
                const data: Company = responseData.data;
                
                const mappedIndustryType = data.industry
                    ? industryTypeOptions.find((option: { value: string; label: string }) => option.value === data.industry?.code)?.value || ''
                    : '';

                const mappedSubIndustryType = data.sub_industry
                    ? subIndustryOptions.find((option: { value: string; label: string }) => option.value === data.sub_industry?.code)?.value || ''
                    : '';
                
                // Update the state with the fetched data
                setTradeName(data.trade_name);
                setLegalName(data.legal_name || '');
                setIndustry(mappedIndustryType || '');
                setSubIndustry(mappedSubIndustryType);
                setExternalId(data.external_id || '');
                setUbi(data.ubi || '');
                setHasUBI(data.ubi ? true : false);
                setAddress(data.address || '');
                setLatitude(data.latitude || '');
                setLongitude(data.longitude || '');
            } catch (error) {
                // Log the caught error to Sentry
                Sentry.captureException(error);
                console.error('Error fetching data from backend:', error);
            }
        } 

        fetchCurrentCompany();
    }, []);

    // Change handlers for form fields
    ////////
    const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>) => (event: ChangeEvent<HTMLInputElement>) => {  
        setter(event.target.value);
    };

    const handleUbiToggle = () => setHasUBI(prevState => !prevState);

    // Change handler factory for react-select components
    const createSelectChangeHandler = (setter: React.Dispatch<React.SetStateAction<string>>) => (selectedOption: any) => {
        setter(selectedOption ? selectedOption.value : '');
    };

    const handleSave = async () => {
        if (isSubmitted) return; // Prevent multiple submissions

        if (!id) {
            setFormErrorMsg(`Id for current company not found.`);
            setShowModal(true);
            return;
        }

        setError(null);
        setIsSubmitted(true);

        const payload = {
            external_id: externalId || null,
            legal_name: legalName || "",
            trade_name: tradeName || "",
            ubi: ubi || "",
            industry_code: industry || null,
            sub_industry_code: subIndustry || null,
            latitude: Number(latitude),
            longitude: Number(longitude),
            address: address,
        };
        console.log('payload:', payload);
        
        if (legalName) {
            try {
                const response = await fetch(`${import.meta.env.VITE_COMMERCE_BACKEND_URL}/api/updatecompany?companyId=${id}`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });
        
                const responseData = await response.json();
        
                if (!response.ok) {
                    throw new Error(responseData.error || 'Failed to update company');
                }
        
                console.log('Company updated sucessfully:', responseData);
                await fetchCompanies(); // ✅ Refresh company list after adding
                // Return to Company dashboard
                setInteractionsYear(currentFisYear.toString());
                navigateBack();
            } catch (error) {
                // Log the caught error to Sentry
                Sentry.captureException(error);
                console.error('Error sending data to backend:', error);
                setError('There was an issue saving the interaction. Please try again later.');
            }
        }
    };

    const handleCancel = () => {
        // Return to Company dashboard
        navigateBack();
    }

    // load script for google maps autocomplete widget and set address state to selected location
    useEffect(() => {
        
        const loader = new Loader({
            apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
            version: 'weekly',
            libraries: ['places'], // Include 'places' library for geocoding and places autocomplete
        });

        loader.load().then(() => {
            if (autocompleteRef.current && window.google && window.google.maps) {
                const autocomplete = new window.google.maps.places.Autocomplete(autocompleteRef.current, {
                    types: ['geocode'], // Specify 'geocode' to get all possible addresses
                    componentRestrictions: { country: 'us' } // Restrict results to a specific country if needed
                });

                autocomplete.addListener('place_changed', () => {
                    const place = autocomplete.getPlace();
                    if (place.formatted_address) {
                        // setSelectedPlace(place);
                        setAddress(place.formatted_address);

                        const lat = place.geometry.location.lat();
                        const lng = place.geometry.location.lng();

                        setLatitude(lat.toString());
                        setLongitude(lng.toString());

                        console.log('Latitude:', lat);
                        console.log('Longitude:', lng);
                    }
                });
            }
        });

    }, []);

    return (
        <>
            <BackButtonHeader navigateBack={navigateBack} name='Company'/> 
            <div className="m-2 p-4">
                {/* Edit Business Details */}
                <div className="mb-8 border border-solid border-color-tableGray rounded-[4px]">
                    <div className='bg-gotham-gray border-b border-solid border-titanium-white mb-5 rounded-t-md'>
                        <h2 className="text-white text-sm font-semibold py-3 ml-7">Edit Company Details</h2>
                    </div>
                    
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-1 ml-3'>
                        {/* Company Name */}
                        <div className="ff-container">
                            <label htmlFor="inputCompany" className="text-sm mb-2 sm:mb-0 sm:mr-4 font-semibold w-48">
                                Company Trade Name:
                            </label>
                            <input
                                id="inputCompany"
                                type="text"
                                value={tradeName}
                                placeholder='Enter Company Name'
                                onChange={handleInputChange(setTradeName)}
                                className="text-sm pl-4 w-full pr-4 py-2 border border-solid border-tableGray rounded-[4px] focus:outline-none focus:border-blue-500 h-10 appearance-none"
                            />
                        </div>
                        {/* Company Legal Name */}
                        <div className="ff-container">
                            <label htmlFor="inputCompanyLegal" className="text-sm mb-2 sm:mb-0 sm:mr-4 font-semibold text-balance w-48">
                                Company Legal Name:
                            </label>
                            <div className='flex flex-col w-full'>
                                <input
                                    id="inputCompanyLegal"
                                    type="text"
                                    value={legalName}
                                    placeholder='Enter Company Legal Name'
                                    onChange={handleInputChange(setLegalName)}
                                    className={`text-sm pl-4 w-full pr-4 py-2 border border-solid ${isSubmitted && !legalName ? 'border-red-500' : 'border-tableGray'} rounded-[4px] focus:outline-none focus:border-blue-500 h-10 appearance-none`}
                                />
                                <p className="text-red-500 text-sm">{`${isSubmitted && !legalName ? 'Company Legal Name is required.' : ''}`}</p>
                            </div>
                        </div>
                        {/* No UBI available */}
                        <div className="ff-container h-14">
                            <label htmlFor="inputHasUbi" className="text-sm mb-2 sm:mb-0 sm:mr-6 font-semibold w-36">
                                No UBI Available:
                            </label>
                            <button
                                id="inputHasUbi"
                                type="button"
                                onClick={handleUbiToggle}
                                className={`w-14 h-7 rounded-full border border-gray-300 focus:outline-none relative overflow-hidden transition-all duration-300 ${!hasUBI ? 'bg-blue-500' : 'bg-gray-300'} focus:ring-2 focus:ring-blue-500 focus:outline-none`}
                            >
                                <span
                                    className={`absolute left-0 top-0 bg-white w-7 h-7 rounded-full transition-all duration-300 transform ${!hasUBI ? 'translate-x-7' : 'translate-x-0'}`}
                                />
                            </button>
                        </div>
                        {!hasUBI &&
                            <br></br>
                        }
                        {/* UBI */}
                        {hasUBI && 
                        <div className="ff-container">
                            <label htmlFor="inputUBI" className="text-sm mb-2 sm:mb-0 sm:mr-4 font-semibold w-48">
                                Company UBI:
                            </label>
                            <div className="flex flex-col w-full">
                                <p className="text-sm text-gotham-gray">
                                    Don't know the Company UBI? You can look it up manually <a href="https://secure.dor.wa.gov/gteunauth/_/" className="text-blue-500 underline" target="_blank" rel="noopener noreferrer">here</a>.
                                </p>
                                <input
                                    id="inputUBI"
                                    type="text"
                                    value={ubi}
                                    placeholder='Enter Company UBI'
                                    onChange={handleInputChange(setUbi)}
                                    className={`text-sm pl-4 w-full pr-4 py-2 border border-solid  rounded-[4px] focus:outline-none focus:border-blue-500 h-10 appearance-none ${isSubmitted && hasUBI && !ubi ? 'border-red-500': 'border-tableGray'}`}
                                />
                                <p className="text-red-500 text-sm">{`${isSubmitted && hasUBI && !ubi ? 'UBI is required. Please Enter Company UBI.' : ''}`}</p>
                            </div>
                        </div>
                        }
                        <div className="ff-container">
                            <label htmlFor="selectIndustry" className="text-sm mb-2 sm:mb-0 sm:mr-4 font-semibold w-48">
                                Industry:
                            </label>
                            <div className="relative w-full">
                                <div className='flex flex-col w-full'>
                                    <Select
                                        id="selectIndustryType"
                                        value={industryTypeOptions.find((option: any) => option.value === industry)}
                                        onChange={createSelectChangeHandler(setIndustry)}
                                        options={industryTypeOptions}
                                        placeholder="Select Industry"
                                        styles={customStylesShorterXL()}
                                        isClearable
                                    />
                                </div>
                            </div>
                        </div>
                        {!industry && <br></br>}
                        {(industry) && 
                        <div className="ff-container">
                            <label htmlFor="selectSubIndustry" className="text-sm mb-2 sm:mb-0 sm:mr-4 font-semibold w-48">
                                Sub-Industry:
                            </label>
                            <div className="relative w-full">
                                <div className='flex flex-col w-full'>
                                    <Select
                                        id="selectSubIndustryType"
                                        value={subIndustryOptions.find(option => option.value === subIndustry)}
                                        onChange={createSelectChangeHandler(setSubIndustry)}
                                        options={subIndustryOptions}
                                        placeholder="Select Sub-Industry"
                                        styles={customStylesShorterXL()}
                                        isClearable
                                    />
                                </div>
                            </div>
                        </div>
                        }
                        
                        <div className="ff-container">
                            <label htmlFor="inputAddress" className="text-sm mb-2 sm:mb-0 sm:mr-4 font-semibold w-48">
                                Address:
                            </label>
                            <div className='relative w-full flex flex-col'>
                                <input
                                    id="inputAddress"
                                    ref={autocompleteRef}
                                    type="text"
                                    value={address}
                                    placeholder='Enter Address'
                                    onChange={handleInputChange(setAddress)}
                                    className="w-full pr-20 pl-2 py-2 border border-solid border-tableGray text-sm rounded-[4px] focus:outline-none focus:border-blue-500 h-10 appearance-none truncate"
                                />
                                {/* <p className="text-red-500 text-sm">{`${isSubmitted && !address && ((latitude === '0' || !latitude) || (longitude === '0' || !longitude))  ? 'Address and/or Latitude and Longitude is required. Please Choose to Enter an Address and/or Latitude and Longitude.' : ''}`}</p> */}
                            </div>
                        </div>
                        <br></br>
                        <div className="ff-container">
                            <label htmlFor="inputLatitude" className="text-sm mb-2 sm:mb-0 sm:mr-4 font-semibold w-48">
                                Latitude:
                            </label>
                            <input
                                id="inputLatitude"
                                type="text"
                                value={latitude}
                                placeholder='Enter Latitude'
                                onChange={handleInputChange(setLatitude)}
                                className="text-sm pl-4 w-full pr-4 py-2 border border-solid border-tableGray rounded-[4px] focus:outline-none focus:border-blue-500 h-10 appearance-none"
                            />
                        </div>
                        <div className="ff-container">
                            <label htmlFor="inputLongitude" className="text-sm mb-2 sm:mb-0 sm:mr-4 font-semibold w-48">
                                Longitude:
                            </label>
                            <input
                                id="inputLongitude"
                                type="text"
                                value={longitude}
                                placeholder='Enter Longitude'
                                onChange={handleInputChange(setLongitude)}
                                className="text-sm pl-4 w-full pr-4 py-2 border border-solid border-tableGray rounded-[4px] focus:outline-none focus:border-blue-500 h-10 appearance-none"
                            />
                        </div>
                        <div className="ff-container">
                            <label htmlFor="inputExternalId" className="text-sm mb-2 sm:mb-0 sm:mr-4 font-semibold w-48">
                                CRM Record ID:
                            </label>
                            <input
                                id="inputExternalId"
                                type="text"
                                value={externalId}
                                placeholder='Optional - link this company to your CRM record'
                                onChange={handleInputChange(setExternalId)}
                                className="text-sm pl-4 w-full pr-4 py-2 border border-solid border-tableGray rounded-[4px] focus:outline-none focus:border-blue-500 h-10 appearance-none"
                            />
                        </div>
                    </div>
                    <FormErrorModal
                        isOpen={showModal}
                        onClose={() => setShowModal(false)}
                        message={formErrorMsg}
                    />
                    <div className="flex items-center border-solid border-t border-color-tableGray">
                        {/* Display the server error message if it exists */}
                        {error && (
                            <div className="text-red-500 text-sm mt-5 ml-7">
                                {error}
                            </div>
                        )}
                        <button
                            id="save-company"
                            onClick={handleSave}
                            className='ml-7 mt-5 mb-3 w-36 h-8 text-center text-white bg-gotham-gray border border-solid border-gotham-gray rounded-[4px] focus:border-blue-500 focus:outline-none hover:bg-blue-400 hover:border-blue-600 cursor-pointer'
                            // disabled={!isFormValid}
                        >
                            Save
                        </button>
                        <button
                            id="cancel-company"
                            onClick={handleCancel}
                            className="ml-7 mt-5 mb-3 w-36 h-8 text-center text-gotham-gray bg-white border border-solid border-gotham-gray rounded-[4px] focus:outline-none focus:border-red-400 hover:bg-red-400 hover:border-red-500 hover:text-white cursor-pointer"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EditCompany;